import { CopyOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Col, Form, Radio, RadioChangeEvent, Row, Table, Typography, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { FormInstance } from 'antd/lib/form';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectUser } from '../../../auth/states/userSlice';
import { Fixed2 } from '../../../common/utils/common.utils';
import { FormButton } from '../../../components/common/FormItem/FormItems';
import InvoiceMoneyReceiptModal from '../../../components/common/Invoice/InvoiceMoneyReceiptModal';
import { useGetAllAgentProfileQuery } from '../../Client/Agents_Profile/api/endpoints/agentProfileEndPoints';
import { useGetAllAirportQuery } from '../../Configuration/airport/endpoints/airportEndpoints';
import { IReissueFormSubmit } from '../Type/InoviceReissue.interface';
import InvoiceReIssueSubTotal from './InvoiceReIssueSubTotal';
import { getReIssuePostFormatData } from '../utils/reissueFormatDataForPost';
import { usePostInvoiceReIssueAirticketMutation } from '../api/endpoints/reIssueAirTicketInvoiceEndpoints';
import { useCheckCreditLimitMutation } from '../../Client/Client/api/endpoints/clientEndpoints';
import { getAgentCommissionRate } from '../../IATA_AIR_TICKET/utils/ts/getAgentCommissionRate';
import InvPostIsDiscountIsPrevDue from '../../IATA_AIR_TICKET/Components/InvPostIsDiscountIsPrevDue';

type Props = {
  invoices: IReissueFormSubmit[];
  setInvoices: React.Dispatch<React.SetStateAction<IReissueFormSubmit[]>>;
  form: FormInstance<any>;
  setEditIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
  editIndex?: number;
};

export default function PostOverviewNonCom({
  invoices,
  setInvoices,
  form,
  setEditIndex,
  editIndex,
}: Props) {
  // @STATES
  const [billingForm] = Form.useForm();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [updateAndPrev, setUpdateAndPrev] = useState<boolean>(false);

  // @POST API
  const [postInvoiceReIssueAirticket, { isError, isLoading, error, isSuccess, data: postIdData }] =
    usePostInvoiceReIssueAirticketMutation();

  const [checkCreditLimit, { isLoading: limiLoading }] = useCheckCreditLimitMutation();

  // @AIRPORT DATA
  const { data: airport } = useGetAllAirportQuery();
  const selectAirport = airport?.data;

  // // @VARIABLES
  const client_price = invoices.reduce((a, b) => a + Fixed2(b?.airticket_client_price), 0);
  const agent_profit = invoices.reduce((a, b) => a + Fixed2(b?.airticket_profit), 0);
  const purchase_price = invoices.reduce((a, b) => a + Fixed2(b?.airticket_purchase_price), 0);
  const profit = invoices.reduce((a, b) => a + Fixed2(b?.airticket_profit), 0);

  useEffect(() => {
    billingForm.setFieldValue('invoice_net_total', client_price);
  }, [client_price]);

  // // @HANDLE COPY
  const handleCopy = (index: number) => {
    form.setFieldsValue(invoices[index]);
    setEditIndex(undefined);
    message.info('Copyed');
  };

  // // @HANDLE EDIT
  const handleEdit = (index: number) => {
    form.setFieldsValue(invoices[index]);
    setEditIndex(index);
  };

  // // @HANDLE DELETE
  const handleDelete = (index: number) => {
    setInvoices(invoices.filter((_, i) => i !== index));
    setEditIndex(undefined);
  };

  // @TABLE COLUMNS
  const column: ColumnsType<any> = [
    {
      width: 15,
      title: 'SL',
      render(_, __, index) {
        return <>{index + 1}</>;
      },
    },
    {
      title: 'Ticket No',
      dataIndex: 'airticket_ticket_no',
      key: 'airticket_ticket_no',
    },
    {
      title: 'Sales',
      dataIndex: 'airticket_client_price',
      key: 'airticket_client_price',
      render(value, record, index) {
        return Fixed2(value);
      },
      align: 'right',
    },
    {
      title: 'Purchase',
      dataIndex: 'airticket_purchase_price',
      key: 'airticket_purchase_price',
      render(value, record, index) {
        return Fixed2(value);
      },
      align: 'right',
    },
    {
      title: 'Profit',
      dataIndex: 'airticket_profit',
      key: 'airticket_profit',
      render(value, record, index) {
        return Fixed2(value);
      },
      align: 'right',
    },
    {
      title: 'Action',
      render(value, record, index) {
        return (
          <div>
            <Button title={'Copy'} onClick={() => handleCopy(index)} size='small' type='text'>
              <CopyOutlined />
            </Button>
            <Button title={'Edit'} onClick={() => handleEdit(index)} size='small' type='text'>
              <EditOutlined />
            </Button>
            <Button title={'Delete'} onClick={() => handleDelete(index)} size='small' type='text'>
              <DeleteOutlined />
            </Button>
          </div>
        );
      },
    },
  ];

  // @HANDLE SUBMIT
  const handleSubmit = (data: any) => {
    const formatedData = getReIssuePostFormatData({
      invoices,
      billing_form: billingForm,
      user_id: user?.user_id as number,
      selectAirport,
    });

    checkCreditLimit({
      amount: formatedData.invoice_info.invoice_net_total,
      combClient: formatedData.invoice_info.invoice_combclient_id,
    }).then((res: any) => {
      if (res?.data?.status === 'LIMIT_OK') {
        postInvoiceReIssueAirticket(formatedData);
      } else {
        message.error('Client limit has been exceeded');
      }
    });
  };

  // // @AFTER CREATE SHOW ALERT
  useEffect(() => {
    if (isSuccess && postIdData?.data) {
      message.success('Invoice Added Successfully');

      updateAndPrev
        ? navigate(`/reissueairticket/details/${postIdData?.data?.invoice_id}`, {
            state: location?.pathname,
          })
        : navigate('/reissueairticket');
    }
  }, [isSuccess, postIdData]);

  // @SHOW ERROR
  useEffect(() => {
    if (isError) message.error('Something went wrong, please try again!');
  }, [isError]);

  // GET ALL AGENT
  const { data: agentData } = useGetAllAgentProfileQuery();
  const getAllAgentProfile = agentData?.data;

  useEffect(() => {
    // @HANDLE AGENT COMMISSION
    if (invoices[invoices?.length - 1]?.invoice_agent_id) {
      const agentCom =
        getAgentCommissionRate({
          allAgents: getAllAgentProfile,
          invoice_agent_id: invoices[invoices?.length - 1]?.invoice_agent_id,
        }) || 0;
      const commission = Fixed2((agentCom / 100) * (agent_profit || 0));

      billingForm.setFieldsValue({
        invoice_agent_com_amount: commission,
      });
    } else {
      billingForm.setFieldValue('invoice_agent_com_amount', undefined);
    }
  }, [invoices]);

  return (
    <Row gutter={[10, 0]}>
      <Table
        style={{ width: '100%' }}
        size='small'
        columns={column}
        dataSource={invoices}
        pagination={false}
        bordered
        rowClassName={(record, index) => (index === editIndex ? 'record-edit-active' : '')}
        summary={(_) => {
          return (
            <Table.Summary.Row style={{ background: '#B6BBC4' }}>
              <Table.Summary.Cell colSpan={2} index={0}>
                <Typography style={{ textAlign: 'center', fontWeight: 'bold' }}>
                  Total :{' '}
                </Typography>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3}>
                <Typography.Text>{Fixed2(client_price)}</Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align='right'>
                <Typography.Text>{Fixed2(purchase_price)}</Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5}>
                <Typography.Text>{Fixed2(profit)}</Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6}></Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />

      <Form
        form={billingForm}
        onFinish={handleSubmit}
        layout='vertical'
        style={{ marginTop: '50px' }}
        disabled={!invoices?.length}
      >
        <InvoiceReIssueSubTotal
          isAgentComission={!invoices[invoices?.length - 1]?.invoice_agent_id}
          billingForm={billingForm}
          client_price={client_price}
        />

        <InvPostIsDiscountIsPrevDue billingForm={billingForm} />

        {/* ADD MONEY RECEIPT */}
        <InvoiceMoneyReceiptModal
          billingForm={billingForm}
          client_price={client_price}
          disabled={!invoices?.length}
        />

        {/* @AIRTICKET EIDT BUTTON */}
        <Row gutter={[10, 10]}>
          <Col>
            <FormButton
              loading={!updateAndPrev && (isLoading || limiLoading)}
              disabled={!invoices?.length}
              label='CREATE'
              onClick={() => {
                setUpdateAndPrev(false);
              }}
            />
          </Col>
          <Col>
            <FormButton
              loading={updateAndPrev && (isLoading || limiLoading)}
              disabled={!invoices?.length}
              label='CREATE & PREVIEW'
              onClick={() => {
                setUpdateAndPrev(true);
              }}
            />
          </Col>
        </Row>
      </Form>
    </Row>
  );
}
